import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PUBLIC_BRAND_NAME, stripD28dFromPublicBrand, getSanitizedPublicBrandName } from '../utils/branding';
import { useFrontendConfig } from '../context/FrontendConfigContext';
import { useI18n } from '../context/useI18n';
import { pickLocalized } from '../utils/localizedField';
import {
  applyDocumentFavicon,
  applyDocumentTitle,
  getAuthTenantBrand,
  pickLoginHeroFromBranding,
  resolveBrandFaviconUrl,
  setAuthTenantBrand,
} from '../utils/documentBrand';
import { fetchPublicTrainerBrand, resolvePublicOrgSlug } from '../utils/gymPublicPath';
import { FOOD_LOGO_HREF } from '../utils/foodPublicBrand';

function trainerLoginHeroFallback(brand, trainerKey) {
  if (!String(trainerKey || '').trim() && !String(brand?.brand_slug || '').trim()) return {};
  const name = String(brand?.brand_name || '').trim();
  const welcome = String(brand?.welcome_message || '').trim();
  return {
    line_white: 'ENTRENAMIENTO',
    line_yellow_1: name ? name.toUpperCase() : '',
    line_yellow_2: '',
    subtitle: welcome || 'Rutinas y seguimiento con tu entrenador.',
  };
}

export default function AuthLayout({
  title,
  subtitle,
  children,
  wide = false,
  orgSlug: orgSlugProp = '',
  trainerSlug = '',
  variant = 'd28d',
}) {
  const isFoodRegister = variant === 'food';
  const { config, brandName: cfgBrand } = useFrontendConfig();
  const { lang } = useI18n();
  const location = useLocation();
  const auth = config?.auth || {};
  const [tenantBrand, setTenantBrand] = useState(() => {
    if (String(trainerSlug || '').trim()) return null;
    return getAuthTenantBrand();
  });

  const trainerKey = String(trainerSlug || '').trim();
  const orgSlug = isFoodRegister || trainerKey
    ? ''
    : (String(orgSlugProp || '').trim()
      || resolvePublicOrgSlug({ pathname: location.pathname, search: location.search }));

  useEffect(() => {
    let cancelled = false;

    const applyBrand = (next) => {
      if (!next || cancelled) return;
      setAuthTenantBrand(next);
      setTenantBrand(next);
      try {
        window.dispatchEvent(new CustomEvent('auth-tenant-brand', { detail: next }));
      } catch { /* noop */ }
    };

    if (isFoodRegister) return undefined;

    if (trainerKey) {
      const stored = getAuthTenantBrand();
      if (stored && String(stored.brand_slug || '') === trainerKey) {
        applyBrand(stored);
      }
      (async () => {
        try {
          const next = await fetchPublicTrainerBrand(trainerKey);
          applyBrand(next);
        } catch {
          /* keep platform / stored */
        }
      })();
      return () => { cancelled = true; };
    }

    const stored = getAuthTenantBrand();
    if (stored && (!orgSlug || stored.brand_slug === orgSlug)) {
      applyBrand(stored);
    }

    if (!orgSlug) return undefined;

    (async () => {
      try {
        const trainer = await fetchPublicTrainerBrand(orgSlug);
        if (trainer && !cancelled) {
          applyBrand(trainer);
          return;
        }
      } catch {
        /* no es coach */
      }
      /* Gym virtual retirado: no aplicar marca de gym / GYM VIRTUAL. */
    })();

    return () => { cancelled = true; };
  }, [orgSlug, isFoodRegister, trainerKey]);

  const hero = useMemo(() => pickLoginHeroFromBranding(tenantBrand || {}), [tenantBrand]);

  useEffect(() => {
    if (isFoodRegister) {
      applyDocumentTitle('Food Plan');
      return undefined;
    }
    if (!tenantBrand) return undefined;
    const name = getSanitizedPublicBrandName(hero.brand_name || '', '');
    if (name) applyDocumentTitle(name);
    const favicon = resolveBrandFaviconUrl(tenantBrand);
    if (favicon) applyDocumentFavicon(favicon, tenantBrand);
    else if (tenantBrand?.brand_slug) applyDocumentFavicon('', tenantBrand);
    // No restaurar D28D en cleanup (StrictMode).
    return undefined;
  }, [tenantBrand, hero.brand_name, isFoodRegister]);

  useEffect(() => {
    if (!isFoodRegister || typeof document === 'undefined') return undefined;
    document.body.classList.add('food-register-active');
    return () => {
      document.body.classList.remove('food-register-active');
    };
  }, [isFoodRegister]);

  const displayBrand = getSanitizedPublicBrandName(
    hero.brand_name || cfgBrand,
    PUBLIC_BRAND_NAME,
  );
  const brandParts = String(displayBrand || PUBLIC_BRAND_NAME || 'ECOSISTEMA FIT').split(' ');
  const first = brandParts[0] || 'ECOSISTEMA';
  const rest = brandParts.slice(1).join(' ') || 'FIT';

  const trainerHero = trainerLoginHeroFallback(tenantBrand || hero, trainerKey);
  const useTrainerChrome = Boolean(trainerKey);
  const lineWhite = hero.line_white
    || (useTrainerChrome ? trainerHero.line_white : pickLocalized(auth, 'line_white', lang, 'ENTRENA EN VIVO'));
  const lineYellow1 = hero.line_yellow_1
    || (useTrainerChrome ? trainerHero.line_yellow_1 : pickLocalized(auth, 'line_yellow_1', lang, 'CON LOS MEJORES'));
  const lineYellow2 = hero.line_yellow_2
    || (useTrainerChrome ? trainerHero.line_yellow_2 : pickLocalized(auth, 'line_yellow_2', lang, 'PROFESIONALES'));
  const subtitleHero = hero.subtitle
    || (useTrainerChrome ? trainerHero.subtitle : (stripD28dFromPublicBrand(pickLocalized(auth, 'subtitle', lang, 'La energía de un gimnasio real, en la comodidad de tu casa.'))
      || 'La energía de un gimnasio real, en la comodidad de tu casa.'));

  const brandAccent = String(hero.primary_color || tenantBrand?.primary_color || '').trim();
  const shellStyle = (!isFoodRegister && brandAccent)
    ? {
      '--d28d-yellow': brandAccent,
      '--brand-primary': brandAccent,
      '--auth-accent': brandAccent,
    }
    : undefined;

  const foodHeroBullets = [
    'Seguimiento nutricional diario',
    'Entrenador personal asignado',
    'Chatbot IA nutricional',
  ];

  return (
    <div
      className={isFoodRegister ? 'food-plan-root food-register-shell auth-shell' : 'auth-shell'}
      style={shellStyle}
    >
      {isFoodRegister ? (
        <section className="auth-shell-hero food-register-hero" aria-hidden="false">
          <img
            src={FOOD_LOGO_HREF}
            alt="Food Plan"
            className="food-register-hero-logo"
            width={88}
            height={88}
          />
          <h1 className="food-register-hero-title">Food Plan</h1>
          <p className="food-register-hero-sub">Tu plan nutricional inteligente</p>
          <ul className="food-register-hero-list">
            {foodHeroBullets.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      ) : (
        <section className="auth-shell-hero" aria-hidden="false">
          <p className="auth-logo" style={{ marginBottom: '2rem' }}>
            <span className="brand-d28d">{first}</span> {rest}
          </p>
          <h1 className="auth-hero-title">
            <span className="line-white">{lineWhite}</span>
            {lineYellow1 ? <span className="line-yellow">{lineYellow1}</span> : null}
            {lineYellow2 ? <span className="line-yellow">{lineYellow2}</span> : null}
          </h1>
          <p className="auth-hero-sub">{subtitleHero}</p>
        </section>
      )}

      <section className="auth-shell-panel">
        <div className={`auth-box ${wide ? 'auth-box-large' : ''}`}>
          {isFoodRegister ? (
            <p className="food-register-panel-brand">
              <img src={FOOD_LOGO_HREF} alt="" width={36} height={36} />
              Food Plan
            </p>
          ) : (
            <p className="auth-logo">
              <span className="brand-d28d">{first}</span> {rest}
            </p>
          )}
          {title ? <h2 className="auth-title">{title}</h2> : null}
          {subtitle ? <p className="auth-subtitle">{subtitle}</p> : null}
          {children}
        </div>
      </section>
    </div>
  );
}
